'use client';
import { StaffRole, UserType } from '@baseball-near-you/common';
import type { Team } from '@baseball-near-you/prisma';
import {
  Form,
  FormInput,
  FormPhoneNumberInput,
  FormSubmitButton,
  FormTextarea,
} from '@baseball-near-you/ui';
import { useClaimTeamMutation } from '@bb/components/Settings/Team/ClaimTeam';
import { dayjsExt } from '@bb/lib/dayjs';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { useCreateTeamMutation } from '@bb/lib/mutations/useCreateTeamMutation';
import { useUpdateUserMutation } from '@bb/lib/mutations/useUpdateUserMutation';
import { z } from 'zod';
import type { TeamCreationFormValues } from '../types';
import { FormRoleSelect } from './FormRoleSelect';

export function TeamCreationForm(props: {
  onSuccess: (team: Team) => void;
}) {
  const { createTeam } = useCreateTeamMutation();
  const { userProfile } = useUserProfile();
  const { updateUser } = useUpdateUserMutation({
    showToast: false,
  });

  const { handleClaimTeam } = useClaimTeamMutation();
  return (
    <Form<TeamCreationFormValues>
      onSubmit={async (data) => {
        if (!userProfile?.id) return;

        const newTeam = await createTeam(data);
        await handleClaimTeam({
          teamId: newTeam.id.toString(),
          firstName: userProfile.firstName!,
          lastName: userProfile.lastName!,
          email: userProfile.email,
          teamName: data.name,
          isCreation: true,
          userId: userProfile.id,
          createdAt: dayjsExt().format('MMMM DD, YYYY hh:mm:ss A'),
          phoneNumber: data.phone,
          background: data.relation,
        });
        await updateUser({
          id: userProfile?.id,
          role: data.role,
          type: UserType.Organization,
        });
        props.onSuccess(newTeam);
      }}
      schema={z.object({
        name: z.string().min(1, {
          message: 'Organization name is required',
        }),
        phone: z.string().min(1, {
          message: 'Phone number is required',
        }),
        email: z.string().email({
          message: 'Email address is required',
        }),
        website: z
          .string()
          .url({
            message: 'Invalid website URL - must start with "https://..."',
          })
          .optional(),
        role: z.nativeEnum(StaffRole).nullable(),
        relation: z
          .string()
          .min(1, {
            message: 'Relationship to the team is required',
          })
          .max(2000, {
            message:
              'Relationship to the team must be less than 2000 characters',
          }),
      })}
      initialValues={{
        name: '',
        phone: '',
        email: '',
        website: undefined,
        role: null,
        relation: '',
      }}
      className="space-y-5"
    >
      <FormInput name="name" label="Organization name" />
      <FormPhoneNumberInput name="phone" label="Phone number" />
      <FormInput name="email" label="Email address" />
      <FormInput name="website" label="Website" placeholder="https://..." />
      <FormRoleSelect />
      <FormTextarea
        name="relation"
        label="Relationship to the team"
        maxLength={2000}
      />
      {/* TODO: Add logo upload */}
      <FormSubmitButton label="Submit team creation request" />
    </Form>
  );
}
